import { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSave, FaSpinner, FaYoutube, FaVideo } from 'react-icons/fa';
import { usePortfolio } from '../../context/PortfolioContext';
import { uploadAPI } from '../../utils/api.js';
import VisibilityToggle from './VisibilityToggle';

const emptyChannel = {
  name: '',
  handle: '',
  url: '',
  description: '',
  subscribers: '',
  avatar: '',
  niche: '',
  visible: true
};

const YouTubeEditor = () => {
  const { portfolioData, updatePortfolioData } = usePortfolio();
  const [channels, setChannels] = useState(portfolioData?.youtube || []);
  const [form, setForm] = useState(emptyChannel);
  const [editingIndex, setEditingIndex] = useState(null);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [latestVideos, setLatestVideos] = useState([]);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState('');

  useEffect(() => {
    setChannels(portfolioData?.youtube || []);
  }, [portfolioData]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Add this function for toggling visibility in the channels list
  const handleChannelChange = (index, field, value) => {
    const updatedItems = [...channels];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value,
    };
    setChannels(updatedItems);
  };

  const handleAvatarUpload = async (file) => {
    setError('');
    setMessage('');
    if (!file) return;
    try {
      const response = await uploadAPI.uploadImage(file);
      if (response.success) {
        setForm(prev => ({ ...prev, avatar: response.data.url }));
        setMessage('Avatar uploaded!');
      } else {
        setError(response.error || 'Failed to upload avatar');
      }
    } catch (err) {
      setError('Error uploading avatar');
    }
  };

  const handleEdit = (channel, idx) => {
    setForm(channel);
    setEditingIndex(idx);
    setError('');
    setMessage('');
  };

  const handleCancel = () => {
    setForm(emptyChannel);
    setEditingIndex(null);
    setError('');
    setMessage('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!form.name || !form.handle || !form.url || !form.niche) {
      setError('Name, handle, url, and niche are required.');
      return;
    }
    setIsSaving(true);
    try {
      const newItem = {
        ...form,
        id: form.id || Date.now(),
        visible: form.visible
      };
      if (editingIndex !== null) {
        await updatePortfolioData('youtube-update', newItem, form.id);
      } else {
        await updatePortfolioData('youtube', newItem);
      }
      setForm(emptyChannel);
      setEditingIndex(null);
      setMessage('Channel saved!');
    } catch (err) {
      setError('Error saving channel.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this YouTube channel?')) return;
    setError('');
    setMessage('');
    try {
      await updatePortfolioData('youtube-delete', {}, id);
      setMessage('Channel deleted!');
      if (editingIndex !== null) handleCancel();
    } catch (err) {
      setError('Error deleting channel.');
    }
  };

  // Helper to extract channelId from url or handle
  const getChannelIdFromUrl = async (url, apiKey) => {
    // Try to extract channelId from url
    const channelIdMatch = url.match(/channel\/([a-zA-Z0-9_-]+)/);
    if (channelIdMatch) return channelIdMatch[1];
    // If it's a handle or user, resolve to channelId using API
    if (url.includes('@')) {
      // Handle: use search API
      const handle = url.split('@')[1].replace(/\W.*/, '');
      const resp = await fetch(`https://www.googleapis.com/youtube/v3/search?part=snippet&type=channel&q=${handle}&key=${apiKey}`);
      const data = await resp.json();
      if (data.items && data.items.length > 0) return data.items[0].snippet.channelId;
    }
    // Fallback: try user
    const userMatch = url.match(/user\/([a-zA-Z0-9_-]+)/);
    if (userMatch) {
      const username = userMatch[1];
      const resp = await fetch(`https://www.googleapis.com/youtube/v3/channels?part=id&forUsername=${username}&key=${apiKey}`);
      const data = await resp.json();
      if (data.items && data.items.length > 0) return data.items[0].id;
    }
    return null;
  };

  const fetchLatestVideos = async () => {
    setVideoLoading(true);
    setVideoError('');
    setLatestVideos([]);
    try {
      const url = form.url;
      if (!url || !apiKey) {
        setVideoError('Channel URL and API key are required.');
        setVideoLoading(false);
        return;
      }
      const channelId = await getChannelIdFromUrl(url, apiKey);
      if (!channelId) {
        setVideoError('Could not resolve channel ID from URL.');
        setVideoLoading(false);
        return;
      }
      const resp = await fetch(`https://www.googleapis.com/youtube/v3/search?key=${apiKey}&channelId=${channelId}&part=snippet,id&order=date&maxResults=3`);
      const data = await resp.json();
      if (data.items) {
        setLatestVideos(data.items.filter(item => item.id.kind === 'youtube#video'));
      } else {
        setVideoError('No videos found or API error.');
      }
    } catch (err) {
      setVideoError('Error fetching videos.');
    } finally {
      setVideoLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 bg-gray-900 rounded-2xl shadow-lg">
      <h3 className="text-2xl font-bold text-primary mb-6 flex items-center gap-2"><FaYoutube /> Manage YouTube Channels</h3>
      {/* API Key input and fetch videos button */}
      <div className="mb-4 flex flex-col md:flex-row gap-4 items-center">
        <input
          type="text"
          className="w-full md:w-1/2 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="YouTube Data API Key (not saved)"
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
        />
        <button
          type="button"
          className="px-4 py-2 bg-primary text-white rounded-lg flex items-center gap-2 hover:bg-primary-dark disabled:opacity-50"
          onClick={fetchLatestVideos}
          disabled={!form.url || !apiKey || videoLoading}
        >
          <FaVideo /> {videoLoading ? 'Fetching...' : 'Fetch Latest 3 Videos'}
        </button>
      </div>
      {/* Show latest videos preview */}
      {videoError && <div className="text-red-400 text-sm mb-2">{videoError}</div>}
      {latestVideos.length > 0 && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {latestVideos.map(video => (
            <a key={video.id.videoId} href={`https://youtube.com/watch?v=${video.id.videoId}`} target="_blank" rel="noopener noreferrer" className="block bg-gray-800 rounded-lg overflow-hidden shadow hover:shadow-lg transition">
              <img src={video.snippet.thumbnails.medium.url} alt={video.snippet.title} className="w-full h-32 object-cover" />
              <div className="p-2 text-white text-sm font-medium line-clamp-2">{video.snippet.title}</div>
            </a>
          ))}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4 mb-8">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Channel Name</label>
            <input type="text" name="name" value={form.name} onChange={handleChange} className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="e.g. Dhiraj AI" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Handle</label>
            <input type="text" name="handle" value={form.handle} onChange={handleChange} className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="e.g. @dhirajai" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Niche/Category</label>
            <input type="text" name="niche" value={form.niche} onChange={handleChange} className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="e.g. AI, Tech, Vlogs" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Subscribers</label>
            <input type="number" name="subscribers" value={form.subscribers} onChange={handleChange} className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="e.g. 10000" min="0" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Channel URL</label>
          <input type="text" name="url" value={form.url} onChange={handleChange} className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="https://youtube.com/@dhirajai" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
          <textarea name="description" value={form.description} onChange={handleChange} rows={2} className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary resize-vertical" placeholder="Describe your channel..." />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Avatar (optional)</label>
          {form.avatar && (
            <div className="mb-2 flex items-center gap-2">
              <img src={form.avatar} alt="Avatar" className="h-16 w-16 rounded-full border border-gray-700 object-cover" />
              <button type="button" onClick={() => setForm(prev => ({ ...prev, avatar: '' }))} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
            </div>
          )}
          <input type="file" accept="image/*" className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary/20 file:text-primary hover:file:bg-primary/30" onChange={e => { const file = e.target.files[0]; if (file) handleAvatarUpload(file); }} />
        </div>
        <VisibilityToggle
            checked={form.visible}
            onChange={e => setForm({ ...form, visible: e.target.checked })}
          loading={false}
          />
        {error && <div className="text-red-400 text-sm">{error}</div>}
        {message && <div className="text-green-400 text-sm">{message}</div>}
        <div className="flex gap-4 justify-end">
          <button type="submit" className="px-6 py-2 bg-primary text-white rounded-lg flex items-center gap-2 hover:bg-primary-dark disabled:opacity-50" disabled={isSaving}>
            {isSaving ? <FaSpinner className="animate-spin" /> : (editingIndex !== null ? <FaEdit /> : <FaPlus />)}
            {isSaving ? 'Saving...' : (editingIndex !== null ? 'Update' : 'Add')}
          </button>
          {editingIndex !== null && (
            <button type="button" onClick={handleCancel} className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800">Cancel</button>
          )}
        </div>
      </form>
      <div className="mt-8">
        <h4 className="text-lg font-semibold text-white mb-4">All YouTube Channels</h4>
        <div className="grid gap-4">
          {channels.length === 0 && <div className="text-gray-400">No channels yet.</div>}
          {channels.map((channel, idx) => (
            <div key={channel.id || idx} className="bg-gray-800 rounded-xl p-4 flex items-center gap-4 shadow">
              <img src={channel.avatar || '/youtube/default-avatar.svg'} alt={channel.name} className="h-12 w-12 rounded-full object-cover border border-primary" />
              <div className="flex-1">
                <div className="font-bold text-white text-lg">{channel.name}</div>
                <div className="text-primary font-semibold">{channel.handle} &bull; {channel.niche}</div>
                <div className="text-gray-400 text-sm">{channel.subscribers} subscribers</div>
                <div className="text-gray-300 text-sm mt-1">{channel.description}</div>
                <a href={channel.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm mt-1 inline-block">Visit Channel</a>
              </div>
              <button onClick={() => handleEdit(channel, idx)} className="text-yellow-400 hover:text-yellow-600 mr-2"><FaEdit /></button>
              <button onClick={() => handleDelete(channel.id)} className="text-red-400 hover:text-red-600"><FaTrash /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default YouTubeEditor; 